import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { MagnifyingGlass, Cpu, Stack, Plus, Trash, X, PlugsConnected, DownloadSimple, Play, Flask, Copy } from "@phosphor-icons/react";

const PROTOCOLS = ["GPSBull", "Volty", "Atlanta", "Acute", "TrackNow", "RDM", "Traxsmart", "KeyTech", "AIS140", "Concox", "Teltonika"];
const TYPES = ["Truck", "Trailer", "Tractor", "Car", "Puller", "Tanker", "Bus"];

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [conn, setConn] = useState(null);
  const [brands, setBrands] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [q, setQ] = useState("");
  const [showBrands, setShowBrands] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [activate, setActivate] = useState(null); // device being activated
  const [newBrand, setNewBrand] = useState({ name: "", protocol: "GPSBull" });
  const [bulk, setBulk] = useState({ brand_id: "", operator: "Airtel", batch: "", imeis: "" });
  const [busy, setBusy] = useState(false);
  const [fBrand, setFBrand] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fBatch, setFBatch] = useState("");
  const [showBulkAct, setShowBulkAct] = useState(false);
  const [bulkAct, setBulkAct] = useState({ customer_id: "", text: "" });

  const loadDevices = () => api.get("/devices").then((r) => setDevices(r.data.devices));
  const loadBrands = () => api.get("/gps-brands").then((r) => { setBrands(r.data.brands); setCanManage(r.data.can_manage); });
  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data.customers)).catch(() => {});
  useEffect(() => { loadDevices(); loadBrands(); loadCustomers(); api.get("/gateway/connection-info").then((r) => setConn(r.data)).catch(() => {}); }, []);
  const copy = (t) => { navigator.clipboard?.writeText(String(t)); toast.success("Copied"); };

  const addBrand = async () => {
    if (!newBrand.name.trim()) return toast.error("Brand name required");
    try {
      const r = await api.post("/gps-brands", newBrand);
      const listening = r.data.listener?.listening;
      toast.success(`"${r.data.name}" added — port ${r.data.port}${listening ? " (listening)" : ""}`);
      setNewBrand({ name: "", protocol: "GPSBull" }); loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const delBrand = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? Its listener on port ${b.port} stops. Existing devices keep records.`)) return;
    try { await api.delete(`/gps-brands/${b.id}`); toast.success("Brand deleted"); loadBrands(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const testBrand = async (b) => {
    try {
      const r = await api.post(`/gps-brands/${b.id}/test`);
      const d = r.data;
      if (d.ok) toast.success(`${b.name}: decoded ${d.protocol} OK · lat ${d.parsed.lat}, spd ${d.parsed.speed} · port ${d.port}${d.listening ? " listening" : ""}`);
      else toast.error(`${b.name}: ${d.message}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const runBulk = async () => {
    if (!bulk.brand_id) return toast.error("Select a GPS brand");
    const imeis = bulk.imeis.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!imeis.length) return toast.error("Paste at least one IMEI");
    setBusy(true);
    try {
      const r = await api.post("/inventory/bulk", { brand_id: bulk.brand_id, operator: bulk.operator, batch: bulk.batch, imeis });
      toast.success(`${r.data.created}/${r.data.total} devices added (batch ${r.data.batch})`);
      r.data.results.filter((x) => x.error).slice(0, 4).forEach((e) => toast.error(`${e.imei}: ${e.error}`));
      setBulk({ brand_id: "", operator: "Airtel", batch: "", imeis: "" }); setShowBulk(false);
      loadDevices(); loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runBulkActivate = async () => {
    const rows = bulkAct.text.split("\n").map((l) => l.split(",").map((s) => s.trim())).filter((a) => a[0] && a[1])
      .map((a) => ({ imei: a[0], vehicle_number: a[1], vehicle_type: a[2] || "Truck", driver_name: a[3] || "" }));
    if (!rows.length) return toast.error("Add lines: imei,vehicle_number");
    setBusy(true);
    try {
      const payload = { rows };
      if (bulkAct.customer_id) payload.customer_id = bulkAct.customer_id;
      const r = await api.post("/inventory/activate-bulk", payload);
      toast.success(`${r.data.created}/${r.data.total} devices activated`);
      r.data.results.filter((x) => x.error).slice(0, 4).forEach((e) => toast.error(`${e.imei}: ${e.error}`));
      setBulkAct({ customer_id: "", text: "" }); setShowBulkAct(false);
      loadDevices(); loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doActivate = async () => {
    const a = activate;
    if (!a.vehicle_number.trim()) return toast.error("Vehicle number required");
    const payload = { vehicle_number: a.vehicle_number, vehicle_type: a.vehicle_type, driver_name: a.driver_name };
    if (a.custMode === "existing" && a.customer_id) payload.customer_id = a.customer_id;
    if (a.custMode === "new" && a.newName.trim()) payload.new_customer = { name: a.newName, email: a.newEmail, password: a.newPass };
    setBusy(true);
    try {
      await api.post(`/inventory/${a.imei}/activate`, payload);
      toast.success(`${a.vehicle_number.toUpperCase()} activated & in service`);
      setActivate(null); loadDevices(); loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const exportXlsx = () => {
    if (!filtered.length) return toast.error("No devices to export");
    const rows = filtered.map((d) => ({ IMEI: d.imei, Brand: d.brand || d.model, Port: d.port || "", Protocol: d.protocol || "", Firmware: d.fw_version || "", SIM: d.operator || "", Status: d.activation_status || "", Batch: d.batch || "" }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(wb, `device-inventory-${Date.now()}.xlsx`);
    toast.success("Inventory exported to Excel");
  };

  const batches = [...new Set(devices.map((d) => d.batch).filter(Boolean))];
  const filtered = devices.filter((d) => {
    if (q && !(d.imei.includes(q) || (d.model || "").toLowerCase().includes(q.toLowerCase()) || (d.brand || "").toLowerCase().includes(q.toLowerCase()))) return false;
    if (fBrand && (d.brand || d.model) !== fBrand) return false;
    if (fStatus && d.activation_status !== fStatus) return false;
    if (fBatch && d.batch !== fBatch) return false;
    return true;
  });

  const openActivate = (d) => setActivate({ imei: d.imei, vehicle_number: "", vehicle_type: "Truck", driver_name: "", custMode: "existing", customer_id: "", newName: "", newEmail: "", newPass: "" });

  const columns = [
    { key: "imei", header: "IMEI", render: (r) => <span className="font-mono text-xs">{r.imei}</span> },
    { key: "brand", header: "Brand", render: (r) => <span className="font-semibold">{r.brand || r.model}</span> },
    { key: "port", header: "Port", render: (r) => r.port ? <Pill color="#00c8dc">{r.port}</Pill> : "—" },
    { key: "fw_version", header: "Firmware", render: (r) => <Pill color={r.fw_version === "2.4.5" ? "#34C759" : "#FF9500"}>{r.fw_version}</Pill> },
    { key: "operator", header: "SIM" },
    { key: "activation_status", header: "Status", render: (r) => <Pill color={r.activation_status === "NEW" ? "#5AC8FA" : "#34C759"}>{r.activation_status}</Pill> },
    { key: "batch", header: "Batch" },
  ];
  if (canManage) {
    columns.push({ key: "act", header: "", render: (r) => r.activation_status === "NEW"
      ? <button data-testid={`activate-${r.imei}`} onClick={() => openActivate(r)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Play size={12} weight="fill" /> Activate</button>
      : <span className="text-xs text-muted-foreground">in service</span> });
  }

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Device Inventory" subtitle={`${devices.length} devices · ${brands.length} GPS brands`}
        actions={<div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <input data-testid="devices-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search IMEI / brand"
              className="bg-card border border-border rounded-sm pl-7 pr-3 py-1.5 text-sm w-48 focus:ring-1 focus:ring-primary focus:outline-none" />
          </div>
          <button data-testid="export-inventory" onClick={exportXlsx} className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-2 rounded-sm hover:border-primary"><DownloadSimple size={15} weight="bold" /> Export Excel</button>
          {canManage && <button data-testid="manage-brands" onClick={() => setShowBrands(true)}
            className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-2 rounded-sm hover:border-primary"><PlugsConnected size={15} weight="bold" /> GPS Brands</button>}
          {canManage && <button data-testid="bulk-inventory" onClick={() => setShowBulk(true)}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-3 py-2 rounded-sm hover:opacity-90"><Stack size={15} weight="bold" /> Bulk Add</button>}
          {canManage && <button data-testid="bulk-activate" onClick={() => setShowBulkAct(true)}
            className="flex items-center gap-1.5 bg-[#34C759] text-white text-sm font-semibold px-3 py-2 rounded-sm hover:opacity-90"><Play size={15} weight="fill" /> Bulk Activate</button>}
        </div>} />

      {/* Device Connection */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="device-connection-card">
        <div className="flex items-center gap-1.5 label-caps mb-2"><PlugsConnected size={13} className="text-primary" /> Device Connection</div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          {conn?.server_ip && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">Device IP <span className="text-[#FF9500] font-semibold">(use on trackers)</span></span>
              <code className="font-mono bg-background border border-border rounded-sm px-2 py-1 text-xs" data-testid="conn-ip">{conn.server_ip}</code>
              <button data-testid="copy-ip" onClick={() => copy(conn.server_ip)} className="text-muted-foreground hover:text-primary"><Copy size={14} /></button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">Domain</span>
            <code className="font-mono bg-background border border-border rounded-sm px-2 py-1 text-xs" data-testid="conn-host">{conn?.host || "—"}</code>
            <button data-testid="copy-host" onClick={() => copy(conn?.host)} className="text-muted-foreground hover:text-primary"><Copy size={14} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">Default port</span>
            <code className="font-mono bg-background border border-border rounded-sm px-2 py-1 text-xs" data-testid="conn-port">{conn?.tcp_default_port || 5023}</code>
            <button data-testid="copy-port" onClick={() => copy(conn?.tcp_default_port || 5023)} className="text-muted-foreground hover:text-primary"><Copy size={14} /></button>
          </div>
          <span className="text-[11px] text-muted-foreground">GPS trackers must use the <b>Device IP</b> (the domain is proxied and can't carry raw TCP). Format <b>IP : port</b> — each brand below has its own port. No API key needed.</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="inventory-filters">
        <select data-testid="filter-brand" value={fBrand} onChange={(e) => setFBrand(e.target.value)} className="bg-card border border-border rounded-sm px-2 py-1.5 text-xs">
          <option value="">All brands</option>
          {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="bg-card border border-border rounded-sm px-2 py-1.5 text-xs">
          <option value="">All status</option>
          <option value="NEW">NEW</option>
          <option value="ACTIVE">ACTIVE</option>
        </select>
        <select data-testid="filter-batch" value={fBatch} onChange={(e) => setFBatch(e.target.value)} className="bg-card border border-border rounded-sm px-2 py-1.5 text-xs">
          <option value="">All batches</option>
          {batches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        {(fBrand || fStatus || fBatch) && <button onClick={() => { setFBrand(""); setFStatus(""); setFBatch(""); }} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} shown</span>
      </div>

      {brands.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="brand-chips">
          {brands.map((b) => (
            <div key={b.id} className="flex items-center gap-2 bg-card border border-border rounded-sm px-3 py-1.5 text-sm">
              <Cpu size={14} className="text-primary" />
              <span className="font-semibold">{b.name}</span>
              <code className="text-xs font-mono text-muted-foreground">:{b.port}</code>
              <button data-testid={`copy-chip-${b.id}`} onClick={() => copy(`${conn?.server_ip || conn?.host || ""}:${b.port}`)} className="text-muted-foreground hover:text-primary" title="Copy IP:port"><Copy size={12} /></button>
              <Pill color="#00c8dc">{b.device_count}</Pill>
            </div>
          ))}
        </div>
      )}

      <DataTable testid="devices-table" columns={columns} rows={filtered} empty="No devices in inventory — add a GPS brand then bulk add IMEIs" />

      {/* GPS Brands modal */}
      {showBrands && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="brands-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-xl space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">GPS Brands</span>
              <button onClick={() => setShowBrands(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground">Each brand gets a unique 5-digit listener port that goes live on the server. Point that brand's devices to your server IP on this port.</p>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <label className="label-caps text-[9px]">Brand name</label>
                <input data-testid="brand-name" value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} placeholder="e.g. Concox GT06" className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              </div>
              <div className="col-span-5">
                <label className="label-caps text-[9px]">Protocol</label>
                <select data-testid="brand-protocol" value={newBrand.protocol} onChange={(e) => setNewBrand({ ...newBrand, protocol: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
                  {PROTOCOLS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <button data-testid="brand-add" onClick={addBrand} className="col-span-2 bg-primary text-white font-semibold text-sm py-2 rounded-sm flex items-center justify-center"><Plus size={15} weight="bold" /></button>
            </div>
            <div className="space-y-1.5" data-testid="brands-list">
              {brands.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">No brands yet</div>}
              {brands.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-background border border-border rounded-sm px-3 py-2">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-sm">{b.name}</span>
                    <span className="text-xs text-muted-foreground">{b.protocol}</span>
                    <Pill color="#00c8dc">port {b.port}</Pill>
                    <button data-testid={`copy-brand-${b.id}`} onClick={() => copy(`${conn?.server_ip || conn?.host || ""}:${b.port}`)} className="text-muted-foreground hover:text-primary" title="Copy IP:port"><Copy size={13} /></button>
                    <span className="text-xs text-muted-foreground">{b.device_count} devices</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`brand-test-${b.id}`} onClick={() => testBrand(b)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Flask size={13} weight="bold" /> Test</button>
                    <button data-testid={`brand-del-${b.id}`} onClick={() => delBrand(b)} className="text-muted-foreground hover:text-destructive"><Trash size={15} weight="bold" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bulk inventory modal */}
      {showBulk && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="bulk-inventory-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">Bulk Add Inventory</span>
              <button onClick={() => setShowBulk(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            {brands.length === 0 ? <p className="text-sm text-muted-foreground">Add a GPS brand first (GPS Brands button).</p> : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label-caps text-[9px]">GPS Brand</label>
                    <select data-testid="bulk-brand" value={bulk.brand_id} onChange={(e) => setBulk({ ...bulk, brand_id: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
                      <option value="">Select brand…</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name} (:{b.port})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-caps text-[9px]">SIM Operator</label>
                    <input value={bulk.operator} onChange={(e) => setBulk({ ...bulk, operator: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="label-caps text-[9px]">Batch label (optional)</label>
                  <input data-testid="bulk-batch" value={bulk.batch} onChange={(e) => setBulk({ ...bulk, batch: e.target.value })} placeholder="e.g. NOV-2026-LOT1" className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
                </div>
                <div>
                  <label className="label-caps text-[9px]">IMEIs (one per line or comma-separated)</label>
                  <textarea data-testid="bulk-imeis" value={bulk.imeis} onChange={(e) => setBulk({ ...bulk, imeis: e.target.value })} rows={7} placeholder={"865412034567890\n865412034567891"} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-xs font-mono" />
                </div>
                <button data-testid="bulk-inventory-submit" disabled={busy} onClick={runBulk} className="w-full bg-primary text-white font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Adding…" : "Add to Inventory"}</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Activate modal */}
      {showBulkAct && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="bulk-activate-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">Bulk Activate</span>
              <button onClick={() => setShowBulkAct(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground">Put many NEW stock devices into service at once. One line per device: <code className="text-primary">imei,vehicle_number,type,driver</code> (type & driver optional).</p>
            <div>
              <label className="label-caps text-[9px]">Assign to customer (optional)</label>
              <select data-testid="bulkact-customer" value={bulkAct.customer_id} onChange={(e) => setBulkAct({ ...bulkAct, customer_id: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
                <option value="">No customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
              </select>
            </div>
            <textarea data-testid="bulkact-text" value={bulkAct.text} onChange={(e) => setBulkAct({ ...bulkAct, text: e.target.value })} rows={8}
              placeholder={"865412034567890,MH12AB1234,Truck,Rahul\n865412034567891,MH12AB1235,Car"}
              className="w-full bg-background border border-border rounded-sm px-2 py-2 text-xs font-mono" />
            <button data-testid="bulkact-submit" disabled={busy} onClick={runBulkActivate} className="w-full bg-[#34C759] text-white font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Activating…" : "Activate All"}</button>
          </div>
        </div>
      )}

      {/* Activate device modal */}
      {activate && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="activate-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">Activate Device</span>
              <button onClick={() => setActivate(null)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground">IMEI <span className="font-mono text-foreground">{activate.imei}</span> — map to a vehicle{customers.length ? " and customer" : ""} to put it in service.</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="activate-vnum" value={activate.vehicle_number} onChange={(e) => setActivate({ ...activate, vehicle_number: e.target.value })} placeholder="Vehicle number *" className="col-span-2 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <select value={activate.vehicle_type} onChange={(e) => setActivate({ ...activate, vehicle_type: e.target.value })} className="bg-background border border-border rounded-sm px-2 py-2 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <input value={activate.driver_name} onChange={(e) => setActivate({ ...activate, driver_name: e.target.value })} placeholder="Driver (optional)" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setActivate({ ...activate, custMode: "existing" })} className={`text-xs font-semibold px-3 py-1.5 rounded-sm border ${activate.custMode === "existing" ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground"}`}>Existing customer</button>
              <button onClick={() => setActivate({ ...activate, custMode: "new" })} className={`text-xs font-semibold px-3 py-1.5 rounded-sm border ${activate.custMode === "new" ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground"}`}>New customer</button>
            </div>
            {activate.custMode === "existing" ? (
              <select data-testid="activate-customer" value={activate.customer_id} onChange={(e) => setActivate({ ...activate, customer_id: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
                <option value="">No customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
              </select>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <input value={activate.newName} onChange={(e) => setActivate({ ...activate, newName: e.target.value })} placeholder="Name *" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
                <input value={activate.newEmail} onChange={(e) => setActivate({ ...activate, newEmail: e.target.value })} placeholder="Login email" className="bg-background border border-border rounded-sm px-2 py-2 text-sm font-mono" />
                <input type="password" value={activate.newPass} onChange={(e) => setActivate({ ...activate, newPass: e.target.value })} placeholder="Password" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setActivate(null)} className="px-4 py-2 text-sm font-semibold border border-border rounded-sm">Cancel</button>
              <button data-testid="activate-submit" disabled={busy} onClick={doActivate} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-sm disabled:opacity-50">{busy ? "Activating…" : "Activate"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
